<template>
	<div class="home-page">
		<section class="intro-sect">
			<div class="container">
				<div class="intro-sect__wrapper row">
					<div class="intro-sect__content col-12 col-lg-7">
						<h1 v-html="$t('homePage.introSectTitleHtml')"></h1>
						<ul class="intro-sect__ul">
							<li>
								<span v-html="$t('homePage.introSectList.str1')"></span>
							</li>
							<li>
								<span v-html="$t('homePage.introSectList.str2')"></span>
							</li>
							<li class="d-flex">
								<span v-html="$t('homePage.introSectList.str3')"></span>
								<Tooltip class="ml-3">
									<template #title>
										<span
											v-html="
												$t(
													'homePage.introSectList.str3TooltipTitle',
												)
											"
										></span
									></template>
									<div
										v-html="
											$t('homePage.introSectList.str3TooltipInner')
										"
									></div>
								</Tooltip>
							</li>
						</ul>
						<button
							class="intro-sect__btn btn-outlined dark"
							@click.prevent="viewProjects"
						>
							{{ $t('viewProjects') }}
						</button>
					</div>
					<div
						class="intro-sect__slider col-12 col-lg-5"
						v-if="isIntroSwiper"
					>
						<swiper
							class="intro-swiper"
							v-bind="introSwiperOptions"
						>
							<template #wrapper-start>
								<swiper-slide
									class="intro-swiper__slide"
									v-for="(slide, i) in introSlides"
									:key="i"
								>
									<img
										class="intro-swiper__img"
										width="780"
										height="1260"
										:src="slide.img"
										alt=""
									/>
									<a
										class="intro-swiper__link"
										:href="slide.link"
										>{{ $t('homePage.introSectLink') }}</a
									>
								</swiper-slide>
							</template>
							<div class="intro-swiper__btn-prev"></div>
							<div class="intro-swiper__btn-next"></div>
						</swiper>
					</div>
				</div>
			</div>
		</section>

		<section
			class="portfolio-sect"
			id="portfolio"
			ref="portfolio"
		>
			<div class="container">
				<h2 v-html="$t('homePage.portfolioSectTitleHtml')"></h2>
				<ProjectsFilters v-model="filteredProjects" />
			</div>
			<swiper
				class="portfolio-swiper"
				v-if="isPortfolioSwiper"
				v-bind="portfolioSwiperOptions"
			>
				<template #wrapper-start>
					<swiper-slide
						v-for="(project, i) in filteredProjects"
						:key="i"
					>
						<ProjectPreview
							class="portfolio-swiper__item"
							:project="project"
						/>
					</swiper-slide>
				</template>
				<div
					class="portfolio-swiper__btn-prev swiper-prev-1 d-none d-md-flex"
				></div>
				<div
					class="portfolio-swiper__btn-next swiper-next-1 d-none d-md-flex"
				></div>
			</swiper>
			<div
				class="portfolio-swiper__pagination swiper-pagination-1 d-md-none"
			></div>
			<div class="container">
				<div class="portfolio-sect__show-more">
					<router-link
						class="btn-primary bold d-none d-md-inline-flex"
						:to="{ name: 'Projects' }"
					>
						<i class="gradient"></i> {{ $t('homePage.viewPortfolio') }}
					</router-link>
					<router-link
						class="btn-primary d-md-none"
						:to="{ name: 'Projects' }"
					>
						<span class="gradient"></span>
						{{ $t('homePage.viewPortfolio') }}
					</router-link>
				</div>
			</div>
		</section>

		<section class="reviews-sect">
			<div class="container">
				<div class="row align-items-center">
					<div class="col">
						<h2>{{ $t('reviewsPage.title') }}:</h2>
					</div>
					<div class="col-auto">
						<router-link
							class="btn-outlined dark reviews-sect__show-more-btn d-none d-md-inline-flex"
							:to="{ name: 'Reviews' }"
						>
							{{ $t('reviewsPage.allReviews') }}
						</router-link>
						<router-link
							class="reviews-sect__show-more-btn d-md-none"
							:to="{ name: 'Reviews' }"
						>
							{{ $t('reviewsPage.allReviews') }}
							<i class="icon-chevron-right"></i>
						</router-link>
					</div>
				</div>

				<swiper
					class="reviews-swiper"
					v-bind="reviewsSwiperOptions"
					@swiper="reviewsSwiper = $event"
				>
					<template #wrapper-start>
						<swiper-slide
							v-for="(review, i) in reviews"
							:key="i"
						>
							<div
								class="reviews-sect__ceo-comment d-none d-lg-block"
								v-if="review.ceoComment"
							>
								<div class="autor">
									<span class="name">{{
										review.ceoComment.autor
									}}</span>
									({{ review.ceoComment.post }}):
								</div>
								<div
									class="comment"
									v-html="review.ceoComment.comment"
								></div>
							</div>
							<ReviewCard
								:review="review"
								:slideIndex="i"
								@updateHeight="updateReviewsSwiperHeight()"
								@slide-to="slideTo"
							/>
						</swiper-slide>
					</template>
					<div
						class="reviews-swiper__btn-prev swiper-prev-2 d-none d-md-flex"
					></div>
					<div
						class="reviews-swiper__btn-next swiper-next-2 d-none d-md-flex"
					></div>
				</swiper>
				<div
					class="reviews-swiper__pagination swiper-pagination-1 d-none d-md-block"
					slot="pagination"
				></div>
			</div>
		</section>
	</div>
</template>

<script>
import Tooltip from '@/components/Tooltip'
import ProjectPreview from '@/components/ProjectPreview'
import ProjectsFilters from '@/components/ProjectsFilters'
import ReviewCard from '@/components/ReviewCard'
import { Swiper, SwiperSlide } from 'swiper/swiper-vue'
import {
	Grid,
	Pagination,
	Navigation,
	EffectFlip,
	EffectCards,
} from 'swiper/modules'

import 'swiper/swiper.css'
import 'swiper/modules/grid.css'
import 'swiper/modules/pagination.css'
import 'swiper/modules/effect-flip.css'
import 'swiper/modules/effect-cards.css'

const mobileBP = 768
const portfolioSwiperBP = mobileBP
const introSwiperBP = mobileBP

export default {
	components: {
		Swiper,
		SwiperSlide,
		Tooltip,
		ProjectPreview,
		ProjectsFilters,
		ReviewCard,
	},

	data() {
		return {
			isPortfolioSwiper: true,
			reviewsSwiper: null,
			portfolioSwiperMQL: window.matchMedia(
				`(min-width: ${portfolioSwiperBP}px`,
			),
			filteredProjects: [],
			portfolioSwiperOptions: {
				modules: [Grid, Pagination, Navigation],
				spaceBetween: 0,
				slidesPerView: 1,
				slidesPerGroup: 1,
				grid: {
					// Корректная последовательность слайдов
					fill: 'column',
					rows: 2,
				},
				navigation: {
					prevEl: '.portfolio-swiper__btn-prev',
					nextEl: '.portfolio-swiper__btn-next',
				},
				pagination: {
					el: '.portfolio-swiper__pagination',
					clickable: true,
				},
				breakpoints: {
					[portfolioSwiperBP]: {
						spaceBetween: -1,
						slidesPerView: 2,
						slidesPerGroup: 2,
						grid: {
							fill: 'row',
							rows: 2,
						},
					},
					1242: {
						spaceBetween: -1,
						slidesPerView: 3,
						slidesPerGroup: 3,
						grid: {
							fill: 'row',
							rows: 2,
						},
					},
				},
			},
			reviewsSwiperOptions: {
				modules: [EffectFlip, Pagination, Navigation],
				autoHeight: true,
				spaceBetween: 48,
				effect: 'flip',
				navigation: {
					prevEl: '.reviews-swiper__btn-prev',
					nextEl: '.reviews-swiper__btn-next',
				},
				pagination: {
					el: '.reviews-swiper__pagination',
					clickable: true,
				},
			},
			introSlides: [
				{
					link: 'https://sa.design/',
					img: '/img/projects/serdyuk/iphone.png',
				},
				{
					link: 'https://new.pandakemerovo.ru/',
					img: '/img/projects/panda/iphone.png',
				},
				{
					link: 'https://www.ros-color.ru/',
					img: '/img/projects/roscolor/iphone.png',
				},
				// {
				// 	link: 'https://zapadoriginal.ru/',
				// 	img: '/img/projects/zapadoriginal/iphone.png',
				// },
			],
			introSwiperMQL: window.matchMedia(`(min-width: ${introSwiperBP}px`),
			isIntroSwiper: false,
			introSwiperOptions: null,
			introSwiperOptionsMobile: {
				modules: [Navigation],
				slidesPerView: 1,
				navigation: {
					prevEl: '.intro-swiper__btn-prev',
					nextEl: '.intro-swiper__btn-next',
				},
			},
			introSwiperOptionsDesktop: {
				modules: [Navigation, EffectCards],
				effect: 'cards',
				cardsEffect: {
					perSlideOffset: 20,
					rotate: false,
					slideShadows: false,
				},
				navigation: {
					prevEl: '.intro-swiper__btn-prev',
					nextEl: '.intro-swiper__btn-next',
				},
			},
		}
	},

	computed: {
		reviews() {
			return this.$store.getters['reviews/reviewsByLocale']
		},
	},

	methods: {
		openRequestPopup() {
			this.$store.dispatch('general/togglePopup', {
				name: 'requestForm',
				show: true,
			})
		},

		// Для переключения эффекта
		updateIntroSwiper() {
			const isMobile = !this.introSwiperMQL.matches
			this.isIntroSwiper = false
			this.introSwiperOptions = isMobile
				? this.introSwiperOptionsMobile
				: this.introSwiperOptionsDesktop
			this.$nextTick(() => {
				this.isIntroSwiper = true
			})
		},

		// Сам перестраивается с некорректной последовательностью слайдов
		updatePortfolioSwiper() {
			this.isPortfolioSwiper = false
			this.$nextTick(() => {
				this.isPortfolioSwiper = true
			})
		},

		updateReviewsSwiperHeight() {
			this.reviewsSwiper.updateAutoHeight(500)
		},

		slideTo(i) {
			this.reviewsSwiper.slideTo(i)
		},

		viewProjects() {
			this.$refs.portfolio.scrollIntoView({
				behavior: 'smooth',
			})
		},
	},

	mounted() {
		this.portfolioSwiperMQL.addEventListener(
			'change',
			this.updatePortfolioSwiper,
		)

		this.introSwiperMQL.addEventListener('change', this.updateIntroSwiper)
		this.updateIntroSwiper()
	},

	beforeDestroy() {
		this.portfolioSwiperMQL.removeEventListener(
			'change',
			this.updatePortfolioSwiper,
		)

		this.introSwiperMQL.removeEventListener('change', this.updateIntroSwiper)
	},
}
</script>

<style lang="scss" scoped>
@import '@/sass/_variables';

.intro-sect {
	padding-top: 80px;
	position: relative;
	z-index: 1;
	&::after {
		content: '';
		display: block;
		height: 350px;
		background: no-repeat url('~@/assets/intro-sect__img.svg') center bottom /
			100% auto;
	}
	h1,
	.h1 {
		font-size: 64px;
		margin: 0 0 64px;
	}
	&__btn {
		width: 272px;
		border-color: #2f80ed;
	}
	&__ul {
		list-style-type: none;
		padding: 0;
		margin-bottom: 48px;
		font-size: 21px;
		li {
			position: relative;
			padding-left: 60px;
			&:not(:last-child) {
				margin-bottom: 16px;
			}
			&::before {
				content: '—';
				position: absolute;
				left: 0;
				top: -4px;
				font-size: 36px;
				line-height: 1;
				color: #2f80ed;
			}
		}
	}
}

.intro-swiper {
	&__btn-prev,
	&__btn-next {
		position: absolute;
		top: 50%;
		background: no-repeat center / contain;
		z-index: 1;
		cursor: pointer;

		&::before {
			content: '';
			display: block;
		}

		&::after {
			content: '';
			position: absolute;
			background: no-repeat bottom right / 100%;
			pointer-events: none;
		}
	}
	&__btn-prev {
		right: 100%;
		width: 23.5%;
		background-image: url('~@/assets/slider__btn-prev.svg');
		transform: translate(50%, -100%);
		&::before {
			padding-top: 110%;
		}
		&::after {
			bottom: 10%;
			right: 2%;
			width: 173.5%;
			height: 180%;
			background-image: url('~@/assets/slider__btn-prev_shadow.svg');
		}
	}
	&__btn-next {
		left: 100%;
		width: 23.4%;
		background-image: url('~@/assets/slider__btn-next.svg');
		transform: translate(-25%, -25%);
		&::before {
			padding-top: 97%;
		}
		&::after {
			bottom: 2%;
			left: 2%;
			width: 156%;
			height: 206%;
			background-image: url('~@/assets/slider__btn-next_shadow.svg');
		}
	}
	&__slide {
		overflow: visible;
	}
	&__img {
		width: 100%;
		height: auto;
	}
	&__link {
		$angle: 11.5deg;
		position: absolute;
		left: -12%;
		bottom: 28.5%;
		display: flex;
		justify-content: center;
		align-items: center;
		height: 52px;
		width: auto;
		padding-left: 40px;
		padding-right: 40px;
		text-align: center;
		border-radius: 29px;
		background: #53befc;
		color: #060f29;
		font-weight: 600;
		font-size: 18px;
		text-decoration: none;
		box-shadow: 0 14px 14px rgba(#05102a, 0.31);
		transform: rotate($angle);
		overflow: hidden;
		transition: 0.3s;

		// opacity: 0;
		// .swiper-slide-active & {
		// 	opacity: 1;
		// }

		&::before,
		&::after {
			content: '';
			position: absolute;
			left: 0;
			width: 20px;
			height: 100vh;
			background: #fff;
			transform: rotate(($angle * -1));
			animation: 4s gloss infinite ease-in-out;
		}
		&::after {
			width: 10px;
			margin-left: 24px;
		}
	}
}

@keyframes gloss {
	0%,
	100% {
		left: -34px;
	}
	50% {
		left: 100%;
	}
}

@media (max-width: $xl) {
	.intro-sect {
		h1,
		.h1 {
			font-size: 44px;
			margin: 0 0 44px;
		}
	}
}

@media (max-width: $lg) {
	.intro-sect {
		padding-top: 52px;
		&::after {
			margin-top: -350px;
		}
		&__ul {
			font-size: 18px;
			li {
				padding-left: 50px;
				&::before {
					top: -6px;
					font-size: 30px;
				}
			}
		}
	}
	.intro-swiper {
		max-width: 500px;
		padding-top: 76px;
	}
}

@media (max-width: $md) {
	.intro-sect {
		&::after {
			height: 240px;
			background-size: 200% auto;
			background-position: center top;
			margin-top: -175px;
		}
		h1,
		.h1 {
			font-size: 24px;
			margin: 0 0 34px;
		}
		&__btn {
			width: 100%;
		}
		&__ul {
			font-size: 15px;
			font-weight: 500;
			li {
				position: relative;
				padding-left: 32px;
				&:not(:last-child) {
					margin-bottom: 20px;
				}
				&::before {
					content: '\e900';
					font-family: 'globalcode-icons' !important;
					font-size: 26px;
					top: -3px;
					color: $primary;
				}
			}
			.tooltip-wrap {
				position: relative;
				top: -3px;
			}
		}
		&__slider {
			display: flex;
			justify-content: center;
		}
	}

	.intro-swiper {
		flex-shrink: 0;
		width: 100vw;
		&__btn-prev {
			left: 0;
			right: auto;
			width: 16%;
		}
		&__btn-next {
			right: 0;
			left: auto;
			width: 16%;
		}
		&__slide {
			box-sizing: border-box;
			padding-left: 65px;
			padding-right: 65px;
		}
		&__link {
			left: auto;
			right: 50%;
			height: 32px;
			min-width: 38%;
			padding-left: 20px;
			padding-right: 20px;
			background: #00d8ff;
			font-size: 12px;
			&::before,
			&::after {
				content: none;
			}
		}
	}
}

@media (max-width: $sm) {
	.intro-swiper {
		&__btn-next {
			left: auto;
			right: 18px;
		}
		&__btn-prev {
			left: 18px;
			right: auto;
		}
	}
}

@media (min-width: ($md + 0.5)) {
	.intro-swiper {
		&__slide {
			// Неактивные слайды по умолчанию
			opacity: 0;
			filter: blur(4px) saturate(0.7) brightness(0.7);
			transition: 0.3s;
		}
		// Активные слайды: текущий + 2 следующих
		.swiper-slide-active {
			filter: none;
			&,
			& ~ * {
				opacity: 1;
			}
			& + * + * ~ * {
				opacity: 0;
			}
		}
	}
}

@media (min-width: ($lg + 0.5)) {
	.intro-sect {
		h1 {
			margin-right: -160px;
		}
		&__slider {
			margin-bottom: -180px;
		}
	}
	.intro-swiper {
		margin-left: 70px;
	}
}

.portfolio-sect {
	margin-top: -2px;
	padding: 52px 0 48px;
	position: relative;
	h2,
	.h2 {
		font-weight: normal;
		margin: 0 0 88px;
	}
	.portfolio-swiper {
		&__pagination {
			margin-top: 22px;
			margin-bottom: 22px;
		}
	}
	&__show-more {
		margin-top: 86px;
		text-align: center;
	}
}
@media (max-width: $md) {
	.portfolio-sect {
		padding: 72px 0 48px;
		margin-top: -36px;
		h2,
		.h2 {
			margin: 0 0 20px;
		}
		.portfolio-swiper {
			// для slidesPerColumnFill = 'column'
			$h: 300px;
			$gap: 8px;
			height: (($h + $gap) * 2);
			.swiper-slide {
				height: $h !important;
				padding: 0 16px;
				box-sizing: border-box;
				margin-top: $gap !important;
			}
			&__item {
				height: 100%;
			}
		}
		&__show-more {
			margin-top: 0;
			.btn-primary {
				width: 375px;
			}
		}
	}
}

.reviews-sect {
	padding-bottom: 48px;
	&__show-more-btn {
		margin: 80px 0 56px;
		width: 196px;
	}
	&__ceo-comment {
		margin-bottom: 48px;
		.autor {
			font-size: 18px;
			.name {
				font-weight: bold;
				font-size: 21px;
			}
		}
		.comment {
			margin-top: 16px;
			font-size: 16px;
			// white-space: pre-wrap;
		}
	}
	.reviews-swiper {
		width: calc(100% + 92px);
		margin-left: -48px;
		padding: 0 48px;
		&__pagination {
			margin-top: 48px;
		}
		&__btn-prev,
		&__btn-next {
			top: 300px;
		}
	}
}
@media (max-width: $lg) {
	.reviews-sect {
		&__show-more-btn {
			margin: 60px 0 40px;
		}
	}
}
@media (max-width: $md) {
	.reviews-sect {
		padding-bottom: 0px;
		&__show-more-btn {
			display: inline-flex;
			align-items: center;
			width: auto;
			margin: 30px 0 20px;
			height: 32px;
			text-decoration: none !important;
			color: $primary;
			transition: 0.3s;
			font-size: 14px;
			letter-spacing: -0.4px;
			&:hover {
				color: #fff;
			}
			.icon-chevron-right {
				margin-left: 6px;
				font-size: 20px;
			}
		}
	}
}
</style>
